import { Select } from "antd";
import { useRef, useState } from "react";
import { Subscription, subscription } from "../../utils/http";

export interface RemoteSelectOption {
  readonly display: string;
  readonly value: string;
}

export interface RemoteSelectProps {
  readonly value?: string;
  readonly onChange?: (value: string | undefined) => void;
  /** A search-request loader (see useSearchRequest factories): text in, matching options out. */
  readonly request: (search: string) => Promise<RemoteSelectOption[]>;
  readonly placeholder?: string;
  readonly className?: string;
  readonly disabled?: boolean;
}

interface Option {
  readonly label: string;
  readonly value: string;
}

// The standard server-searched combobox for selects over unbounded catalogs (companies,
// questionnaires, …): every keystroke queries the server (debounced), so the dropdown never
// depends on a fixed-page option load that would silently miss entries at scale. Opening the
// dropdown fetches the top-N browse list fresh — scoped requests (e.g. departments of the
// selected company) therefore never serve a stale parent's children.
function RemoteSelect(props: RemoteSelectProps) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  // The in-flight (possibly delay-pending) search — superseded searches are cancelled.
  const pending = useRef<Subscription<RemoteSelectOption[]>>();
  // The chosen option, remembered so its label survives the options list moving on.
  const [selected, setSelected] = useState<Option | undefined>();

  const search = (text: string, delay: number) => {
    pending.current?.unsubscribe();
    setLoading(true);
    const request = subscription(() => props.request(text), delay);
    pending.current = request;
    request
      .then((items) => {
        if (pending.current !== request) return;
        setOptions(items.map((i) => ({ label: i.display, value: i.value })));
        setLoading(false);
      })
      .catch(() => {
        if (pending.current !== request) return;
        setOptions([]);
        setLoading(false);
      });
  };

  // Keep the selected option visible even when the latest search no longer includes it.
  const visibleOptions =
    props.value != null && selected && !options.some((o) => o.value === props.value)
      ? [selected, ...options]
      : options;

  return (
    <Select
      className={props.className}
      disabled={props.disabled}
      placeholder={props.placeholder}
      value={props.value}
      options={visibleOptions}
      loading={loading}
      showSearch
      allowClear
      filterOption={false}
      labelInValue={false}
      onSearch={(text) => search(text, 300)}
      onDropdownVisibleChange={(open) => {
        if (open) search("", 0);
      }}
      onChange={(v) => {
        setSelected(visibleOptions.find((o) => o.value === v));
        props.onChange?.(v);
      }}
    />
  );
}

export default RemoteSelect;
